import { AdminIntro, AdminNav } from "../../components/AdminWorkspace";
import AddIcon from "@material-ui/icons/Add";
import React, { useState, useEffect, useReducer, useContext } from "react";
import { toast } from "react-toastify";
import { makeStyles } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import IconButton from "@material-ui/core/IconButton";
import Tooltip from "@material-ui/core/Tooltip";
import CircularProgress from "@material-ui/core/CircularProgress";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EditIcon from "@material-ui/icons/Edit";
import PeopleOutlineIcon from "@material-ui/icons/PeopleOutline";
import MainContainer from "../../components/MainContainer";
import { StatusBadge, EmptyState } from "../../components/ui";
import {
  usePanelStyles,
  FilterBar,
  SearchField,
  TableCard,
  RowActions,
} from "../../components/FormFields/panel";
import whatsappIcon from "../../assets/nopicture.png";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import UserModal from "../../components/UserModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import toastError from "../../errors/toastError";
import { getBackendUrl } from "../../config";
import { AuthContext } from "../../context/Auth/AuthContext";
import { Avatar } from "@material-ui/core";
import ForbiddenPage from "../../components/ForbiddenPage";
import ChatBubbleOutlineIcon from "@material-ui/icons/ChatBubbleOutline";
import { useHistory } from "react-router-dom";
import usePlans from "../../hooks/usePlans";
import { getTokens } from "../../styles/tokens";

const backendUrl = getBackendUrl();

const reducer = (state, action) => {
  if (action.type === "LOAD_USERS") {
    const users = action.payload;
    const newUsers = [];

    users.forEach((user) => {
      const userIndex = state.findIndex((u) => u.id === user.id);
      if (userIndex !== -1) {
        state[userIndex] = user;
      } else {
        newUsers.push(user);
      }
    });

    return [...state, ...newUsers];
  }

  if (action.type === "UPDATE_USERS") {
    const user = action.payload;
    const userIndex = state.findIndex((u) => u.id === user.id);

    if (userIndex !== -1) {
      state[userIndex] = user;
      return [...state];
    } else {
      return [user, ...state];
    }
  }

  if (action.type === "DELETE_USER") {
    const userId = action.payload;

    const userIndex = state.findIndex((u) => u.id === userId);
    if (userIndex !== -1) {
      state.splice(userIndex, 1);
    }
    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }
};

const useStyles = makeStyles((theme) => {
  const t = getTokens(theme.palette.type, theme.palette.primary.main);
  return {
    grow: { flex: "1 1 auto" },
    // no celular a tabela rola dentro do cartão em vez de espremer as colunas
    table: { minWidth: 820 },
    idCell: { width: 64, color: t.textSecondary, fontVariantNumeric: "tabular-nums" },
    // largura mínima: com `overflowWrap: anywhere` a coluna encolhia até quebrar o nome letra a letra
    person: { display: "flex", alignItems: "center", gap: 12, minWidth: 220 },
    personText: { minWidth: 0 },
    personName: {
      fontWeight: 600,
      color: t.text,
      lineHeight: 1.35,
      overflowWrap: "anywhere",
    },
    personEmail: {
      fontSize: 12.5,
      lineHeight: 1.4,
      color: t.textSecondary,
      overflowWrap: "anywhere",
    },
    userAvatar: {
      flex: "none",
      width: 36,
      height: 36,
      backgroundColor: t.muted,
      border: `1px solid ${t.line}`,
    },
    queues: { display: "flex", flexWrap: "wrap", gap: "4px 12px", maxWidth: 260 },
    queue: {
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      minWidth: 0,
      fontSize: 12.5,
      color: t.textSecondary,
      whiteSpace: "nowrap",
    },
    hours: { whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums", color: t.textSecondary },
    loadMore: { display: "flex", justifyContent: "center", padding: 12 },
  };
});

// na coluna cabem poucas filas: o restante vira "+N" com os nomes no Tooltip
const MAX_QUEUES_IN_CELL = 3;

const Users = () => {
  const classes = useStyles();
  const p = usePanelStyles();
  const history = useHistory();

  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [deletingUser, setDeletingUser] = useState(null);
  const [userModalOpen, setUserModalOpen] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [users, dispatch] = useReducer(reducer, []);
  const { user: loggedInUser, socket } = useContext(AuthContext);
  const companyId = loggedInUser.companyId;

  const [showInternalChat, setShowInternalChat] = useState(false);

  const { getPlanCompany } = usePlans();

  useEffect(() => {
    async function fetchData() {
      if (!companyId) {
        return;
      }

      try {
        setLoading(true);
        const planConfigs = await getPlanCompany(undefined, companyId);

        setShowInternalChat(planConfigs.plan.useInternalChat);
      } catch (err) {
        console.error("Error fetching plan:", err);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam]);

  useEffect(() => {
    setLoading(true);
    const fetchUsers = async () => {
      try {
        const { data } = await api.get("/users/", {
          params: { searchParam, pageNumber },
        });
        dispatch({ type: "LOAD_USERS", payload: data.users });
        setHasMore(data.hasMore);
      } catch (err) {
        toastError(err);
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    };
    fetchUsers();
  }, [searchParam, pageNumber]);

useEffect(() => {
  if (loggedInUser) {
    const companyId = loggedInUser.companyId;
    
    const onCompanyUser = (data) => {
      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "UPDATE_USERS", payload: data.user });
      }
      if (data.action === "delete") {
        dispatch({ type: "DELETE_USER", payload: +data.userId });
      }
    };
    
    socket.on(`company-${companyId}-user`, onCompanyUser);
    
    return () => {
      socket.off(`company-${companyId}-user`, onCompanyUser);
    };
  }
}, [socket, loggedInUser]);

  const handleOpenUserModal = () => {
    setSelectedUser(null);
    setUserModalOpen(true);
  };

  const handleCloseUserModal = () => {
    setSelectedUser(null);
    setUserModalOpen(false);
  };

  const handleSearch = (event) => {
    setSearchParam(event.target.value.toLowerCase());
  };

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setUserModalOpen(true);
  };

  const handleDeleteUser = async (userId) => {
    try {
      await api.delete(`/users/${userId}`);
      toast.success(i18n.t("users.toasts.deleted"));
    } catch (err) {
      toastError(err);
    }
    setDeletingUser(null);
    setSearchParam("");
    setPageNumber(1);
  };

  const loadMore = () => {
    setLoadingMore(true);
    setPageNumber((prevPage) => prevPage + 1);
  };

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      loadMore();
    }
  };

const renderProfileImage = (user) => {
  const buildImageUrl = (userData) => {
    if (!userData.profileImage) {
      return whatsappIcon;
    }
    return `${backendUrl}/public/company${userData.companyId}/user/${userData.profileImage}`;
  };

  // Para o usuário logado, verificar localStorage também
  if (user.id === loggedInUser.id) {
    const savedProfileImage = localStorage.getItem("profileImage");
    const profileImageToUse = savedProfileImage || user.profileImage;
    
    const imageUrl = profileImageToUse 
      ? `${backendUrl}/public/company${user.companyId}/user/${profileImageToUse}`
      : whatsappIcon;

    return (
      <Avatar
        src={imageUrl}
        alt={user.name}
        className={classes.userAvatar}
        onError={(e) => {
          e.target.src = whatsappIcon;
        }}
      />
    );
  }
  
  // Para outros usuários
  return (
    <Avatar
      src={buildImageUrl(user)}
      alt={user.name}
      className={classes.userAvatar}
    />
  );
};

  const renderQueues = (user) => {
    const queues = Array.isArray(user.queues) ? user.queues : [];
    if (queues.length === 0) return <span className={p.cellMuted}>—</span>;
    const visible = queues.slice(0, MAX_QUEUES_IN_CELL);
    const hidden = queues.slice(MAX_QUEUES_IN_CELL);
    return (
      <div className={classes.queues}>
        {visible.map((queue) => (
          <span key={queue.id} className={classes.queue}>
            <span
              className={p.colorDot}
              style={{ backgroundColor: queue.color || "transparent" }}
              aria-hidden="true"
            />
            {queue.name}
          </span>
        ))}
        {hidden.length > 0 && (
          <Tooltip title={hidden.map((queue) => queue.name).join(", ")}>
            <span className={classes.queue}>+{hidden.length}</span>
          </Tooltip>
        )}
      </div>
    );
  };

  const handleCreateChat = async (targetUser) => {
    try {
      setLoading(true);
      const { data } = await api.post("/chats", {
        users: [{ id: loggedInUser.id }, { id: targetUser.id }],
        isGroup: false,
        title: targetUser.name,
      });
      toast.success("Chat criado com sucesso!");
      history.push(`/chats/${data.uuid}`);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleBackfillChats = async () => {
    if (
      !window.confirm(
        "Tem certeza que deseja gerar chats para todos os usuários existentes? Isso pode demorar e criar muitos chats."
      )
    ) {
      return;
    }
    setLoading(true);
    try {
      await api.post("/chats/backfill");
      toast.success("Chats gerados com sucesso!");
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <MainContainer maxWidth>
      <ConfirmationModal
        title={
          deletingUser &&
          `${i18n.t("users.confirmationModal.deleteTitle")} ${
            deletingUser.name
          }?`
        }
        open={confirmModalOpen}
        onClose={() => setConfirmModalOpen(false)}
        onConfirm={() => handleDeleteUser(deletingUser.id)}
      >
        {i18n.t("users.confirmationModal.deleteMessage")}
      </ConfirmationModal>
      <UserModal
        open={userModalOpen}
        onClose={handleCloseUserModal}
        aria-labelledby="form-dialog-title"
        userId={selectedUser && selectedUser.id}
      />
      {loggedInUser.profile === "user" ? (
        <ForbiddenPage />
      ) : (
        <>
          <AdminIntro
            title="Equipe e usuários"
            description="Gerencie quem atende, os perfis de acesso e o expediente de cada pessoa."
            actions={
              <>
                {loggedInUser.profile === "admin" && showInternalChat && (
                  <Button
                    variant="outlined"
                    onClick={handleBackfillChats}
                    disabled={loading}
                  >
                    Gerar Chats Existentes
                  </Button>
                )}
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleOpenUserModal}
                  startIcon={<AddIcon />}
                >
                  {i18n.t("users.buttons.add")}
                </Button>
              </>
            }
          />
          <AdminNav />

          <FilterBar>
            <SearchField
              placeholder={i18n.t("contacts.searchPlaceholder")}
              value={searchParam}
              onChange={handleSearch}
            />
            <span className={classes.grow} />
            <StatusBadge
              color="neutral"
              dot={false}
              label={`${users.length}${hasMore ? "+" : ""} ${
                users.length === 1 ? "usuário" : "usuários"
              }`}
            />
          </FilterBar>

          <TableCard fill onScroll={handleScroll}>
            <Table size="small" className={classes.table}>
              <TableHead>
                <TableRow>
                  <TableCell className={classes.idCell}>
                    {i18n.t("users.table.ID")}
                  </TableCell>
                  <TableCell>{i18n.t("users.table.name")}</TableCell>
                  <TableCell>{i18n.t("users.table.profile")}</TableCell>
                  <TableCell>Filas</TableCell>
                  <TableCell>Expediente</TableCell>
                  <TableCell>{i18n.t("users.table.status")}</TableCell>
                  <TableCell align="right">
                    {i18n.t("users.table.actions")}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <>
                  {users.map((user) => (
                    <TableRow key={user.id}>
                      <TableCell className={classes.idCell}>{user.id}</TableCell>
                      <TableCell>
                        <div className={classes.person}>
                          {renderProfileImage(user)}
                          <div className={classes.personText}>
                            <div className={classes.personName}>{user.name}</div>
                            <div className={classes.personEmail}>{user.email}</div>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <StatusBadge
                          color={user.profile === "admin" ? "brand" : "neutral"}
                          dot={false}
                          label={user.profile === "admin" ? "Administrador" : user.profile === "user" ? "Atendente" : user.profile}
                        />
                      </TableCell>
                      <TableCell>{renderQueues(user)}</TableCell>
                      <TableCell className={classes.hours}>
                        {user.startWork} – {user.endWork}
                      </TableCell>
                      <TableCell>
                        <StatusBadge status={user.online === true ? "online" : "offline"} />
                      </TableCell>
                      <TableCell align="right">
                        <RowActions>
                          <Tooltip title="Abrir chat interno">
                            {/* span: Tooltip não escuta botão desabilitado */}
                            <span>
                              <IconButton
                                size="small"
                                aria-label={`Abrir chat interno com ${user.name}`}
                                onClick={() => handleCreateChat(user)}
                                disabled={!showInternalChat}
                              >
                                <ChatBubbleOutlineIcon />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title="Editar">
                            <IconButton
                              size="small"
                              aria-label={`Editar usuário ${user.name}`}
                              onClick={() => handleEditUser(user)}
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Excluir">
                            <IconButton
                              size="small"
                              aria-label={`Excluir usuário ${user.name}`}
                              onClick={(e) => {
                                setConfirmModalOpen(true);
                                setDeletingUser(user);
                              }}
                            >
                              <DeleteOutlineIcon />
                            </IconButton>
                          </Tooltip>
                        </RowActions>
                      </TableCell>
                    </TableRow>
                  ))}
                  {loadingMore && (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        <CircularProgress size={24} />
                      </TableCell>
                    </TableRow>
                  )}
                  {loading && !loadingMore && users.length === 0 &&
                    Array.from({ length: 4 }).map((_, idx) => (
                      <TableRowSkeleton key={`skeleton-${idx}`} columns={7} />
                    ))}
                </>
              </TableBody>
            </Table>
            {!loading && users.length === 0 && (
              <EmptyState
                icon={<PeopleOutlineIcon />}
                title="Nenhum usuário encontrado"
                message={
                  searchParam
                    ? "Nenhum usuário corresponde à busca."
                    : "Adicione a primeira pessoa da equipe."
                }
                action={
                  !searchParam && (
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={<AddIcon />}
                      onClick={handleOpenUserModal}
                    >
                      {i18n.t("users.buttons.add")}
                    </Button>
                  )
                }
              />
            )}
            {/* com poucas linhas o cartão não rola e a rolagem infinita não dispara: o botão garante o acesso */}
            {hasMore && !loadingMore && (
              <div className={classes.loadMore}>
                <Button variant="outlined" onClick={loadMore} disabled={loading}>
                  {i18n.t("contacts.loadMore")}
                </Button>
              </div>
            )}
          </TableCard>
        </>
      )}
    </MainContainer>
  );
};

export default Users;
